import Login from '../components/Login';
import Vue from 'vue';
import VueRouter from 'vue-router';
import About from '../components/Static/About';
import License from '../components/Static/License';
import Downloads from '../components/Static/Downloads';
import NotExistingWorkspace from '../components/Workspace/Layout/NotExistingWorkspace';
import CustomSection from '../components/Static/CustomSection';
import {get} from 'lodash';

export function mapExtraPagesAccess(access, defaultAccess) {
  return get({
    public: {publicAccess: true},
    guest: {guestAccess: true},
    registered: {registeredAccess: true},
    admin: {adminAccess: true},
  }, access, defaultAccess || {});
}

export function routerBuilder(auth, config) {
  Vue.use(VueRouter);

  const routes = [
    {path: '/', name: 'index', redirect: '/workspaces'},
    {path: '/login', name: 'login', component: Login, meta: {publicAccess: true}},
    {path: '/login/:token', name: 'login-with-token', component: Login},
    {path: '/login/:token/:redirect', name: 'login-with-token-and-redirect', component: Login},
    {path: '/downloads', name: 'downloads', component: Downloads, meta: {guestAccess: true}},
    {path: '/about', name: 'about', component: About, meta: {publicAccess: true}},
    {path: '/license', name: 'license', component: License, meta: {publicAccess: true}},
    {
      path: '/workspaces',
      name: 'workspaces',
      component: () => import(/* webpackChunkName: "workspace-selector" */ '../components/Workspace/Layout/WorkspaceSelector'),
      meta: {guestAccess: true},
      children: [
        {
          path: 'import',
          name: 'workspace-import',
          component: () => import(/* webpackChunkName: "workspace-management" */ '../components/Workspace/WorkspaceImport'),
        },
        {
          path: 'share/:accessToken',
          name: 'workspace-share',
          component: () => import(/* webpackChunkName: "workspace-management" */ '../components/Workspace/WorkspaceShare'),
        },
      ],
    },
    {
      path: '/workspaces/not-existing',
      name: 'not-existing-workspace',
      component: NotExistingWorkspace,
      meta: {guestAccess: true},
    },
    {
      path: '/workspaces/:id',
      name: 'workspace',
      component: () => import(/* webpackChunkName: "workspace-layout" */ '../components/Workspace/Layout/WorkspaceLayout'),
      meta: {guestAccess: true},
      children: [
        {
          path: 'export',
          name: 'workspace-export',
          component: () => import(/* webpackChunkName: "workspace-management" */ '../components/Workspace/WorkspaceExport'),
        },
        {
          path: 'edit',
          name: 'workspace-edit',
          component: () => import(/* webpackChunkName: "workspace-management" */ '../components/Workspace/WorkspaceEdit'),
          meta: {guestAccess: true, registeredAccess: true},
        },
      ],
    },
    {
      path: '/runs',
      name: 'runs',
      component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/RunList'),
      children: [
        {
          path: ':id',
          name: 'run-details',
          component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/RunDetails'),
          children: [
            {
              path: 'sets/new',
              name: 'scenario-set-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/SetEditor'),
            },
            {
              path: 'sets/:name/edit',
              name: 'scenario-set-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/SetEditor'),
            },
            {
              path: 'parameters/new',
              name: 'scenario-parameter-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'parameters/:name/edit',
              name: 'scenario-parameter-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'variables/new',
              name: 'scenario-variable-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'variables/:name/edit',
              name: 'scenario-variable-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'equations/new',
              name: 'scenario-equation-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'equations/:name/edit',
              name: 'scenario-equation-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/ParameterEditor'),
            },
            {
              path: 'timeseries/new',
              name: 'scenario-timeseries-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/TimeseriesEditor'),
            },
            {
              path: 'timeseries/:name/edit',
              name: 'scenario-timeseries-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/TimeseriesEditor'),
            },
            {
              path: 'layer/new',
              name: 'scenario-layer-new',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/TimeseriesEditor'),
            },
            {
              path: 'layer/:name/edit',
              name: 'scenario-layer-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/TimeseriesEditor'),
            },
            {
              path: 'metadata/:name/edit',
              name: 'scenario-metadata-edit',
              component: () => import(/* webpackChunkName: "scenario-management" */ '../components/Scenario/MetadataEditor'),
            },
          ],
        },
      ],
    },
    {
      path: '/docs',
      name: 'docs',
      component: () => import(/* webpackChunkName: "others" */ '../components/Docs.vue'),
      meta: {guestAccess: true},
    },
    {
      path: '/uploads',
      name: 'uploads',
      component: () => import(/* webpackChunkName: "uploads" */ '../components/Uploads/Uploads.vue'),
      children: [
        {
          path: 'jobs/:id',
          component: () => import(/* webpackChunkName: "uploads" */ '../components/Uploads/JobDetails.vue'),
          props: route => ({jobId: parseInt(route.params.id, 10)}),
          children: [
            {path: 'details', name: 'job-details'},
            {path: 'log', name: 'job-log'},
          ],
        },
      ],
      meta: {checkAccess: auth => auth.canUploadFiles()},
    },
    {
      path: '/snapshots',
      name: 'snapshots',
      component: () => import(/* webpackChunkName: "snapshots" */ '../components/Snapshots/Snapshots.vue'),
      children: [
        {
          path: 'jobs/:id',
          component: () => import(/* webpackChunkName: "snapshots" */ '../components/Uploads/JobDetails.vue'),
          props: route => ({
            jobId: parseInt(route.params.id, 10),
            detailsRoute: 'snapshot-job-details',
            logRoute: 'snapshot-job-log',
            backRoute: 'snapshots',
          }),
          children: [
            {path: 'details', name: 'snapshot-job-details'},
            {path: 'log', name: 'snapshot-job-log'},
          ],
        },
      ],
      meta: {checkAccess: auth => auth.canGenerateSnapshots()},
    },
    {
      path: '/caches',
      name: 'caches',
      component: () => import(/* webpackChunkName: "caches" */ '../components/Caches/CacheManager'),
      meta: {checkAccess: auth => auth.isAdmin},
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import(/* webpackChunkName: "others" */ '../components/Profile.vue'),
      meta: {registeredAccess: true},
    },
    {
      path: '/feedback',
      name: 'feedback',
      component: () => import(/* webpackChunkName: "others" */ '../components/Feedback.vue'),
      meta: {registeredAccess: true},
    },
    ...(config.extraMenu || []).map(m => ({
      path: `/extra/${m.name}`,
      name: m.name,
      component: CustomSection,
      props: {source: `extra/${m.title}`, sectionClass: m.sectionClass || 'container py-3'},
      meta: mapExtraPagesAccess(m.access, {guestAccess: true}),
    })),
  ];

  const setDefaultAccess = (routes, parentMeta = {}) => {
    routes.forEach(route => {
      route.meta = route.meta || {};
      const meta = {...parentMeta, ...route.meta};
      const {checkAccess, publicAccess, guestAccess, registeredAccess, adminAccess} = meta;
      if (!checkAccess) {
        if (publicAccess) {
          route.meta.checkAccess = _ => true;
        } else if (guestAccess) {
          route.meta.checkAccess = auth => auth.isGuest || auth.isRegistered;
        } else if (registeredAccess) {
          route.meta.checkAccess = auth => auth.isRegistered;
        } else if (adminAccess) {
          route.meta.checkAccess = auth => auth.isAdmin;
        } else {
          route.meta.checkAccess = auth => auth.isRegistered;
        }
      }
      if (route.children) {
        setDefaultAccess(route.children, meta);
      }
    });
  };
  setDefaultAccess(routes);

  const linkActiveClass = 'nav-item active';

  const router = new VueRouter({routes, linkActiveClass});

  router.beforeEach((to, from, next) => {
    if (to.name === null) {
      next({name: 'index', replace: true});
    } else if (to.name.startsWith('login-with-token')) {
      auth.isLoggingIn = true;
      auth.loginWithToken(to.params.token).then(() => {
        auth.isLoggingIn = false;
        if (to.params.redirect) {
          next(decodeURIComponent(to.params.redirect));
        } else {
          next({name: 'index', replace: true});
        }
      });
    } else if (to.name.startsWith('login') && auth.isLoggedIn && !auth.isLoggingOut) {
      // restrict opening login for logged users
      next({name: 'index', replace: true});
    } else if (to.name === 'login' && from.query.redirect) {
      next({name: 'login', query: from.query});
    } else if (to.name === 'login') {
      next();
    } else {
      // custom permissions checks (may return promises)
      Promise.all(to.matched.map(async m => !m.meta.checkAccess || m.meta.checkAccess(auth)))
        .then(checks => {
          if (checks.some(c => c)) {
            next();
          } else {
            next({name: 'login', query: {redirect: to.fullPath}});
          }
        });
    }
  });

  return router;
}
